import React from "react";
import { store } from "react-notifications-component";
import {
  KUIKA_ENV_CONFIG_MESSAGE_TYPE,
  type KuikaEnvConfig,
  type KuikaEnvConfigMessage
} from "../shared/utilty/frontend-url";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";

let waitingServiceWorker: ServiceWorker | null = null;
let onUpdateAvailableCallback: (() => void) | null = null;

/**
 * Register a callback to be notified when a new app version is available
 */
function onUpdateAvailable(callback: () => void) {
  onUpdateAvailableCallback = callback;
  // If update is already waiting, call immediately
  if (waitingServiceWorker) {
    callback();
  }
}

/**
 * Apply the pending update - activates the new service worker and reloads the page
 */
function applyUpdate(): void {
  if (!waitingServiceWorker) return;

  // We rely on `controllerchange` to know the new SW is in charge, but in
  // Chrome that event only fires after the new SW calls `clients.claim()` —
  // and we deliberately do NOT claim (lazy-loaded chunks from the old bundle
  // would 404 against the new precache mid-session). Firefox happens to fire
  // `controllerchange` when the old SW becomes redundant via skipWaiting, so
  // the previous code worked there but not in Chrome — including offline,
  // where the click on "Refresh" appeared to do nothing.
  //
  // Fallback: if the event hasn't fired by the time skipWaiting + activate
  // would have completed (well under a second on any healthy SW), force the
  // reload. The reload navigation goes through the registration's active
  // worker, which by then is the new one — so we get the new bundle even
  // though we never claimed.
  let reloaded: boolean = false;
  const reloadOnce: () => void = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, { once: true });
  waitingServiceWorker.postMessage("SKIP_WAITING");

  const fallbackReloadDelayMs: number = 1500;
  window.setTimeout(reloadOnce, fallbackReloadDelayMs);
}

function showUpdateNotification() {
  const isTurkish = localStorage.getItem("ml") === "tr_TR";

  const message = React.createElement(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "8px",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'"
      }
    },
    React.createElement("span", null, isTurkish ? "Yeni uygulama sürümü mevcut." : "New app version available."),
    React.createElement(
      "button",
      {
        onClick: () => applyUpdate(),
        style: {
          padding: "4px 12px",
          border: "none",
          borderRadius: "4px",
          backgroundColor: "#fff",
          color: "#333",
          cursor: "pointer",
          fontWeight: "bold",
          alignSelf: "flex-start"
        }
      },
      isTurkish ? "Yenile" : "Refresh"
    )
  );

  store.addNotification({
    title: isTurkish ? "Güncelleme!" : "Update!",
    message,
    type: "info",
    container: "bottom-right",
    animationIn: ["animated", "flipInX"],
    animationOut: ["animated", "fadeOut"],
    dismiss: {
      duration: 0,
      pauseOnHover: true,
      showIcon: true
    }
  });
}

function notifyUpdateAvailable(worker: ServiceWorker) {
  if (waitingServiceWorker === worker) return;

  waitingServiceWorker = worker;
  showUpdateNotification();
  if (onUpdateAvailableCallback) {
    onUpdateAvailableCallback();
  }
}

function waitForPageSettled(): Promise<void> {
  return new Promise((resolve) => {
    function afterLoad() {
      setTimeout(resolve, 5000);
    }
    if (document.readyState === "complete") {
      afterLoad();
    } else {
      window.addEventListener("load", afterLoad, { once: true });
    }
  });
}

/**
 * Initialize app update monitoring via service worker
 */
export async function initAppUpdateMonitoring() {
  // Service Workers only work with http:, https:, or localhost
  // Skip for custom protocols (e.g., kuika:// in Electron)
  const protocol = window.location.protocol;
  if (protocol !== "http:" && protocol !== "https:") {
    return;
  }

  if (!("serviceWorker" in navigator)) {
    return;
  }

  // In dev server mode no sw.js is emitted — skip update monitoring entirely.
  if (process.env.DEV_SERVER === "true") {
    return;
  }

  try {
    const swUrl = `${KuikaAppManager.GetFrontEndUrl()}/sw.js`;

    // Idempotent: if the SW is already registered with the same scriptURL, the
    // browser returns the existing registration and triggers an update check.
    //
    // We deliberately do NOT unregister-and-reregister when controller is null.
    // controller-null with no installing/waiting worker is the spec-mandated
    // state on every hard reload (Shift+Reload) and on every load with
    // DevTools "Bypass for network"/"Update on reload" enabled. Calling
    // unregister() in that case (a) doesn't actually free the SW in Chromium
    // until every controlled client closes (workbox#1687, "SW resurrection"),
    // (b) leaves Firefox in a degraded state per workbox#1783, and
    // (c) on Safari produces empty caches because the new install begins
    // before the old cache is reclaimed. The page just won't be SW-controlled
    // for that load — which is what the user asked for by hard-reloading.
    const registration = await navigator.serviceWorker.register(swUrl);

    void forwardEnvConfigToServiceWorker(registration);

    let settled = false;
    let pendingUpdateWorker: ServiceWorker | null = null;

    const handleInstalledWorker = (worker: ServiceWorker) => {
      if (!navigator.serviceWorker.controller) {
        worker.postMessage("SKIP_WAITING");
        return;
      }

      if (settled) {
        notifyUpdateAvailable(worker);
      } else {
        pendingUpdateWorker = worker;
      }
    };

    // Controlled pages keep the old SW until the user explicitly refreshes.
    // Uncontrolled pages (first install, hard reload, DevTools bypass) can
    // activate the installed worker immediately because no current controller
    // owns this page's bundle graph.
    if (registration.waiting) {
      handleInstalledWorker(registration.waiting);
    }
    if (registration.installing) {
      const installingWorker = registration.installing;
      installingWorker.addEventListener("statechange", () => {
        if (installingWorker.state === "installed") {
          handleInstalledWorker(installingWorker);
        }
      });
    }

    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed") {
            handleInstalledWorker(newWorker);
          }
        });
      }
    });

    // After the page has fully loaded, any future update is a background update.
    await waitForPageSettled();
    settled = true;
    if (pendingUpdateWorker) {
      notifyUpdateAvailable(pendingUpdateWorker);
      pendingUpdateWorker = null;
    }

    // Update checks are network-bound. Calling registration.update() while
    // offline triggers a failing fetch for sw.js; if a previous attempt also
    // managed to start an install before going offline, that install can fail
    // its precache fetch pass (workbox precacheAndRoute is all-or-nothing) and
    // wedge the previously-good precache via cleanupOutdatedCaches. Skip the
    // call when the user-visible online state says we can't usefully run it.
    //
    // update() can also reject (e.g., the registration was uninstalled by the
    // browser while we held a reference, or sw.js is no longer reachable
    // because the runtime restarted on a different port). Those promise
    // rejections used to surface as Uncaught (in promise) noise; we swallow
    // them here and stop polling once the registration is dead, since there's
    // nothing useful left to do with this object.
    let updateIntervalId: ReturnType<typeof setInterval> | null = null;
    let updateChecksAlive: boolean = true;

    const stopUpdateChecks: () => void = (): void => {
      updateChecksAlive = false;
      if (updateIntervalId !== null) {
        clearInterval(updateIntervalId);
        updateIntervalId = null;
      }
    };

    const triggerUpdateCheck: () => Promise<void> = async (): Promise<void> => {
      if (!updateChecksAlive) return;
      const browserOnline: boolean = typeof navigator === "undefined" ? true : navigator.onLine;
      if (!browserOnline) return;
      try {
        await registration.update();
      } catch (error: unknown) {
        console.warn("[sw-update] update check failed, stopping further checks", error);
        stopUpdateChecks();
      }
    };

    updateIntervalId = setInterval(triggerUpdateCheck, 60 * 1000);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        void triggerUpdateCheck();
      }
    });
  } catch (error) {
    console.error("App update monitoring initialization failed", error);
  }
}

interface WindowWithEnv {
  _env_?: KuikaEnvConfig;
}

// Re-sent on every page load so a redeploy with a different env-config refreshes
// the SW too (the SW persists across loads).
async function forwardEnvConfigToServiceWorker(registration: ServiceWorkerRegistration): Promise<void> {
  const env: KuikaEnvConfig | undefined = (window as WindowWithEnv)._env_;
  if (!env) return;

  const message: KuikaEnvConfigMessage = { type: KUIKA_ENV_CONFIG_MESSAGE_TYPE, env };

  const post = (worker: ServiceWorker | null): boolean => {
    if (!worker) return false;
    try {
      worker.postMessage(message);
      return true;
    } catch {
      return false;
    }
  };

  if (post(navigator.serviceWorker.controller)) return;
  if (post(registration.active)) return;
  if (post(registration.waiting)) return;

  const installing: ServiceWorker | null = registration.installing;
  if (!installing) return;

  // Remove the listener on first usable state OR on redundant — otherwise it
  // leaks for the lifetime of a SW that never reaches activated (e.g. install
  // rejected, or replaced by a newer registration).
  const onChange: () => void = (): void => {
    const state: ServiceWorkerState = installing.state;
    if (state === "installed" || state === "activated") {
      post(installing);
      installing.removeEventListener("statechange", onChange);
    } else if (state === "redundant") {
      installing.removeEventListener("statechange", onChange);
    }
  };
  installing.addEventListener("statechange", onChange);
}
